import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';

import TableComponent from '../components/Table';
import CustomDateRangePicker from '../components/common/DatePicker/CustomDateRangePicker';
import TransactionLink from '../components/ui/TransactionLink';

import useAPI from '../hooks/useAPI';
import { formatCurrency } from '../utility/number.utils';
import styles from './RequestedToken.module.scss';
import { convertToCSV } from '../lib/lib';
import apiInstance from '../config/axios';

interface RequestedToken {
  id: string;
  tokenType: string;
  amount: number;
  requestedBy: string;
  status: string;
  createdAt: string;
  transactionNo?: string;
  tokenAmount?: number;
  transactionDate?: string;
}

interface IPaginationObject {
  limit: number;
  page: number;
  status: string;
  from: string;
  to: string;
}

interface IRequestedTokenResponse {
  result: {
    tokenTransactionHistory: RequestedToken[];
    totalTokenTransactionHistory: number;
    totalPages: number;
  };
}

const StatusOptions = [
  { label: 'Requested', value: 'PENDING' },
  { label: 'Accepted', value: 'SUCCESS' },
  { label: 'Rejected', value: 'FAILED' },
];

const MAX_DATE_RANGE_DAYS = 90;

export default function RequestedTokenHistory() {
  // Pagination and filter states
  const [paginationData, setPaginationData] = useState<IPaginationObject>({
    page: 1,
    limit: 10,
    status: '',
    from: '',
    to: '',
  });
  const [isDownloading, setIsDownloading] = useState(false);

  // Date range states
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string>('');

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationData.page));
    params.append('limit', String(paginationData.limit));
    if (paginationData.status) {
      params.append('status', paginationData.status);
    }
    if (paginationData.from) {
      params.append('fromDate', paginationData.from);
    }
    if (paginationData.to) {
      params.append('toDate', paginationData.to);
    }
    return `/token/transaction/history/get/request-history?${params.toString()}`;
  };

  // Fetch requested token history using useAPI
  const { data: apiResponse, isLoading } = useAPI<
    void,
    IRequestedTokenResponse
  >({
    url: buildApiUrl(),
    method: 'GET',
    dependencies: [
      paginationData.page,
      paginationData.limit,
      paginationData.status,
      paginationData.from,
      paginationData.to,
    ],
    onSuccess: () => {},
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          'Failed to fetch token request history',
      );
    },
  });

  const requestedTokenData = apiResponse?.result?.tokenTransactionHistory || [];
  const totalCount = apiResponse?.result?.totalTokenTransactionHistory || 0;

  // Validate date range (max 90 days)
  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined,
  ): boolean => {
    if (!start || !end) return true;

    const diffInDays = moment(end).diff(moment(start), 'days');
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }
    setDateError('');
    return true;
  };

  // Handle date range change with validation
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      if (validateDateRange(startDate, date)) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          from: moment(startDate).format('YYYY-MM-DD'),
          to: moment(date).format('YYYY-MM-DD'),
        }));
      }
    } else if (!date) {
      setDateError('');
      setPaginationData(prev => ({
        ...prev,
        page: 1,
        from: '',
        to: '',
      }));
    }
  };

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      status: value === 'all' ? '' : value,
    }));
  };

  // Handle pagination change from TableComponent
  const handleFetchMoreData = (pageSize: number, page: number) => {
    setPaginationData(prev => ({
      ...prev,
      limit: pageSize,
      page: page,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setDateRange([null, null]);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateError('');
    setPaginationData({
      page: 1,
      limit: 10,
      status: '',
      from: '',
      to: '',
    });
  };

  const getStatusValue = (tokenType: string, status: string) => {
    if (tokenType === 'RECEIVE') {
      if (status === 'PENDING') return 'REQUESTED';
      if (status === 'SUCCESS') return 'ACCEPTED';
      if (status === 'FAILED') return 'REJECTED';
    } else if (tokenType === 'TRANSFER') {
      return 'TRANSFERRED';
    } else if (tokenType === 'MINT') {
      return 'MINTED';
    } else if (tokenType === 'BURN') {
      return 'BURNT';
    } else {
      return tokenType;
    }
  };

  const columns = [
    {
      label: 'Transaction No.',
      id: 'transactionId',
      width: 300,
      renderCell: (row: any) => {
        return row?.transactionNo ? (
          <TransactionLink transactionNo={row?.transactionNo} />
        ) : (
          '-'
        );
      },
    },
    {
      label: '# of Tokens',
      id: 'tokenAmount',
      renderCell: (row: any) => (
        <span>
          {formatCurrency(row?.tokenAmount ?? 0, {
            currency: '',
            decimals: 0,
          })}
        </span>
      ),
    },
    // {
    //   label: 'Sender Email',
    //   id: 'createdByEmail',
    //   renderCell: (row: any) => (
    //     <span>
    //       {row?.tokenType==='RECEIVE' ? '-': row?.createdByEmail}
    //     </span>
    //   ),
    // },
    // {
    //   label: 'Receiver Email',
    //   id: 'receiverByEmail',
    //   renderCell: (row: any) => <span>{row?.receiverByEmail}</span>,
    // },
    {
      label: 'Transaction Date & Time',
      id: 'transactionDate',
      renderCell: (row: any) =>
        row?.transactionDate
          ? moment(row.transactionDate).format('MMM D YYYY HH:mm:ss')
          : '-',
    },
    {
      label: 'Status',
      id: 'tokenType',
      renderCell: (row: any) => (
        <span className={`${row?.tokenType === 'BURN' ? 'text-danger' : ''}`}>
          {getStatusValue(row?.tokenType, row?.status)}
        </span>
      ),
    },
  ];

  const hasActiveFilters =
    paginationData.status || paginationData.from || paginationData.to;
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', paginationData.status || '');
      params.append('fromDate', paginationData.from);
      params.append('toDate', paginationData.to);

      const response = await apiInstance.get(
        `/token/transaction/history/download/request-history?${params.toString()}`,
      );

      const transactionHistory = response.data.result.tokenTransactionHistory;

      if (!transactionHistory || transactionHistory.length === 0) {
        toast.error('No data available to download');
        return;
      }

      const csvContent = convertToCSV(transactionHistory);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = moment().format('DDMMYYYYHHmmss');
      link.setAttribute('download', `RequestedToken${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error?.response?.data?.message || 'Failed to download CSV');
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <div>
      <h2 className="fw-bold mb-3">Requested Token</h2>

      <div className="bg-white rounded-3 p-3">
        {/* Filters Section */}
        <div className="d-flex flex-wrap gap-3 align-items-start">
          {/* Date Range Picker */}
          <div style={{ minWidth: '220px' }} className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">
              Transaction Date <span className="text-muted fw-normal"></span>
            </Form.Label>
            <CustomDateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              // @ts-ignore
              setEndDate={handleEndDateChange}
              placeholder="Select date range"
            />
            {dateError && (
              <Form.Text className="text-danger small">{dateError}</Form.Text>
            )}
          </div>

          {/* Status Filter */}
          <div style={{ minWidth: '150px' }}>
            <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
            <Form.Select
              className="rounded-2 custom-input"
              value={paginationData.status || 'all'}
              onChange={e => handleStatusChange(e.target.value)}
            >
              <option value="all">All Status</option>
              {StatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="d-flex flex-column">
              <Form.Label className="small fw-semibold mb-1 custom-filter">
                {/* <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>
              
            </span> */}
              </Form.Label>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClearFilters}
                className="rounded-2 custom-filter-button"
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Download Button */}
          <div className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">&nbsp;</Form.Label>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleDownload}
              disabled={isDownloading}
              className="rounded-2 custom-filter-button"
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </div>
        </div>

        {/* Requested Token Table */}
        <div className={styles.tableContainer}>
          <TableComponent
            data={requestedTokenData}
            columns={columns}
            totalCount={totalCount}
            fetchMore={handleFetchMoreData}
            isServerSidePagination
            perPageSize={paginationData.limit}
            activePage={paginationData.page}
            loading={isLoading}
            customBodyCss="custom-table-requested-token"
          />
        </div>
      </div>
    </div>
  );
}
